import { useEffect } from 'react';
import { usePresenceUsers } from '../lib/presence-api.js';

export interface PresenceModalProps {
  open: boolean;
  onClose: () => void;
}

export function PresenceModal({ open, onClose }: PresenceModalProps): React.JSX.Element | null {
  const { data, isLoading, isError, refetch } = usePresenceUsers(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const users = data?.users ?? [];
  const sorted = [...users].sort((a, b) =>
    a.pseudo.localeCompare(b.pseudo, undefined, { sensitivity: 'base' }),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="presence-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-slate-700 bg-slate-900 p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 id="presence-modal-title" className="text-base font-semibold text-emerald-300">
            Connected users{users.length > 0 ? ` (${users.length})` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded px-2 py-0.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            ✕
          </button>
        </div>

        {isLoading && <p className="text-sm text-slate-400">Loading…</p>}

        {isError && (
          <div className="text-sm text-red-300">
            Failed to load presence.{' '}
            <button
              type="button"
              onClick={() => void refetch()}
              className="underline hover:text-red-200"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && sorted.length === 0 && (
          <p className="text-sm text-slate-400">No one is connected.</p>
        )}

        {!isLoading && !isError && sorted.length > 0 && (
          <ul className="max-h-80 overflow-y-auto divide-y divide-slate-800 rounded border border-slate-800">
            {sorted.map((u) => (
              <li
                key={u.accountId}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-200"
              >
                {u.avatarUrl ? (
                  <img
                    src={u.avatarUrl}
                    alt=""
                    className="h-6 w-6 rounded-full object-cover"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-700 text-xs uppercase text-slate-300"
                  >
                    {u.pseudo.charAt(0)}
                  </span>
                )}
                <span className="truncate">{u.pseudo}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
